/**
 * Organisation Management Page
 * CRUD operations for organisations (Platform Admin only)
 */

import { AlertCircle, Loader2 } from 'lucide-react'
import { useOrganisationManagement } from './organisation-management/use-organisation-management'
import { OrganisationPageHeader } from './organisation-management/OrganisationPageHeader'
import { OrganisationSearchBar } from './organisation-management/OrganisationSearchBar'
import { OrganisationGrid } from './organisation-management/OrganisationGrid'
import { CreateOrganisationModal } from './organisation-management/CreateOrganisationModal'
import { EditOrganisationModal } from './organisation-management/EditOrganisationModal'
import { DeleteOrganisationModal } from './organisation-management/DeleteOrganisationModal'

type Mgmt = ReturnType<typeof useOrganisationManagement>

function matchesQuery(name: string, slug: string, query: string): boolean {
  const lower = query.toLowerCase()
  return name.toLowerCase().includes(lower) || slug.toLowerCase().includes(lower)
}

function OrgModals({ mgmt }: { mgmt: Mgmt }): React.ReactElement {
  return (
    <>
      {mgmt.showCreateModal && (
        <CreateOrganisationModal
          formData={mgmt.formData}
          setFormData={mgmt.setFormData}
          formError={mgmt.formError}
          formLoading={mgmt.formLoading}
          orphanedXeroOrgIds={mgmt.orphanedXeroOrgIds}
          onClose={() => {
            mgmt.setShowCreateModal(false)
          }}
          onSubmit={(e) => {
            void mgmt.handleCreateOrg(e)
          }}
        />
      )}
      {mgmt.showEditModal && mgmt.selectedOrg && (
        <EditOrganisationModal
          formData={mgmt.formData}
          setFormData={mgmt.setFormData}
          formError={mgmt.formError}
          formLoading={mgmt.formLoading}
          onClose={() => {
            mgmt.setShowEditModal(false)
          }}
          onSubmit={(e) => {
            void mgmt.handleUpdateOrg(e)
          }}
        />
      )}
      {mgmt.showDeleteModal && mgmt.selectedOrg && (
        <DeleteOrganisationModal
          org={mgmt.selectedOrg}
          formError={mgmt.formError}
          formLoading={mgmt.formLoading}
          onClose={() => {
            mgmt.setShowDeleteModal(false)
          }}
          onConfirm={() => {
            void mgmt.handleDeleteOrg()
          }}
        />
      )}
    </>
  )
}

export function OrganisationManagementPage(): React.ReactElement {
  const mgmt = useOrganisationManagement()

  if (mgmt.loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#4e5e82]" />
      </div>
    )
  }

  const filteredOrgs = mgmt.organisations.filter((org) =>
    matchesQuery(org.name, org.slug, mgmt.searchQuery)
  )

  return (
    <div className="space-y-6">
      <OrganisationPageHeader
        onAdd={() => {
          mgmt.resetForm()
          mgmt.setShowCreateModal(true)
        }}
      />
      {mgmt.error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-600 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="h-5 w-5" />
          {mgmt.error}
        </div>
      )}
      <OrganisationSearchBar searchQuery={mgmt.searchQuery} setSearchQuery={mgmt.setSearchQuery} />
      <OrganisationGrid
        organisations={filteredOrgs}
        actionMenuOrg={mgmt.actionMenuOrg}
        setActionMenuOrg={mgmt.setActionMenuOrg}
        onEdit={mgmt.openEditModal}
        onDelete={mgmt.openDeleteModal}
        onToggleStatus={(org) => {
          void mgmt.handleToggleStatus(org)
        }}
      />
      <OrgModals mgmt={mgmt} />
    </div>
  )
}

export default OrganisationManagementPage
